import React, { createContext, useContext, useState, useEffect } from 'react';

const REGIONS = {
  IN: {
    code: 'IN',
    name: 'India',
    flag: '🇮🇳',
    currency: 'INR',
    symbol: '₹',
    locale: 'en-IN',
    ark: { min: 98000, max: 168000 },
    arkPro: { min: 149000, max: 268000 },
    intelligence: { blacksite: 499, blacksitePayu: 509.78 },
  },
  US: {
    code: 'US',
    name: 'United States',
    flag: '🇺🇸',
    currency: 'USD',
    symbol: '$',
    locale: 'en-US',
    ark: { min: 1175, max: 2016 },
    arkPro: { min: 1788, max: 3216 },
    intelligence: { blacksite: 5.99, blacksitePayu: 6.19 },
  },
  GB: {
    code: 'GB',
    name: 'United Kingdom',
    flag: '🇬🇧',
    currency: 'GBP',
    symbol: '£',
    locale: 'en-GB',
    ark: { min: 940, max: 1613 },
    arkPro: { min: 1430, max: 2573 },
    intelligence: { blacksite: 4.79, blacksitePayu: 4.99 },
  },
  EU: {
    code: 'EU',
    name: 'Europe',
    flag: '🇪🇺',
    currency: 'EUR',
    symbol: '€',
    locale: 'de-DE',
    ark: { min: 1080, max: 1854 },
    arkPro: { min: 1645, max: 2959 },
    intelligence: { blacksite: 5.49, blacksitePayu: 5.69 },
  },
  AE: {
    code: 'AE',
    name: 'United Arab Emirates',
    flag: '🇦🇪',
    currency: 'AED',
    symbol: 'AED',
    locale: 'ar-AE',
    ark: { min: 4315, max: 7405 },
    arkPro: { min: 6573, max: 11828 },
    intelligence: { blacksite: 21.99, blacksitePayu: 22.49 },
  },
  JP: {
    code: 'JP',
    name: 'Japan',
    flag: '🇯🇵',
    currency: 'JPY',
    symbol: '¥',
    locale: 'ja-JP',
    ark: { min: 176400, max: 302400 },
    arkPro: { min: 268700, max: 483400 },
    intelligence: { blacksite: 899, blacksitePayu: 919 },
  },
  AU: {
    code: 'AU',
    name: 'Australia',
    flag: '🇦🇺',
    currency: 'AUD',
    symbol: 'A$',
    locale: 'en-AU',
    ark: { min: 1820, max: 3122 },
    arkPro: { min: 2773, max: 4990 },
    intelligence: { blacksite: 9.29, blacksitePayu: 9.49 },
  },
  SG: {
    code: 'SG',
    name: 'Singapore',
    flag: '🇸🇬',
    currency: 'SGD',
    symbol: 'S$',
    locale: 'en-SG',
    ark: { min: 1587, max: 2722 },
    arkPro: { min: 2417, max: 4349 },
    intelligence: { blacksite: 7.99, blacksitePayu: 8.19 },
  },
};

// Map country codes to regions
const COUNTRY_TO_REGION = {
  IN: 'IN',
  US: 'US', CA: 'US',
  GB: 'GB',
  DE: 'EU', FR: 'EU', IT: 'EU', ES: 'EU', NL: 'EU', BE: 'EU', AT: 'EU', PT: 'EU', IE: 'EU', FI: 'EU', GR: 'EU',
  AE: 'AE', SA: 'AE', QA: 'AE', BH: 'AE', KW: 'AE', OM: 'AE',
  JP: 'JP',
  AU: 'AU', NZ: 'AU',
  SG: 'SG', MY: 'SG', TH: 'SG', PH: 'SG', ID: 'SG', VN: 'SG',
};

const RegionContext = createContext(null);

export const useRegion = () => {
  const ctx = useContext(RegionContext);
  if (!ctx) throw new Error('useRegion must be used within RegionProvider');
  return ctx;
};

export const RegionProvider = ({ children }) => {
  const [regionCode, setRegionCode] = useState(() => {
    return localStorage.getItem('fozzil-region') || null;
  });
  const [isDetecting, setIsDetecting] = useState(!regionCode);

  // Auto-detect region from timezone/locale first, then optional IP geolocation
  useEffect(() => {
    if (regionCode) return;

    const detectFromTimezone = () => {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      const locale = navigator.language || 'en-US';
      
      if (tz.startsWith('Asia/Kolkata') || tz.startsWith('Asia/Calcutta') || locale.includes('IN')) return 'IN';
      if (tz.startsWith('America/') && locale.includes('en-US')) return 'US';
      if (tz.startsWith('Europe/London') || locale.includes('en-GB')) return 'GB';
      if (tz.startsWith('Europe/')) return 'EU';
      if (tz.startsWith('Asia/Dubai') || tz.startsWith('Asia/Riyadh')) return 'AE';
      if (tz.startsWith('Asia/Tokyo')) return 'JP';
      if (tz.startsWith('Australia/')) return 'AU';
      if (tz.startsWith('Asia/Singapore') || tz.startsWith('Asia/Kuala_Lumpur')) return 'SG';
      
      return null;
    };

    const detected = detectFromTimezone();
    if (detected) {
      setRegionCode(detected);
      localStorage.setItem('fozzil-region', detected);
      setIsDetecting(false);
      return;
    }

    // Fallback: try IP geolocation (free service)
    const detectFromIP = async () => {
      try {
        const res = await fetch('https://get.geojs.io/v1/ip/geo.json', { signal: AbortSignal.timeout(3000) });
        const data = await res.json();
        const countryCode = data.country_code || data.country;
        const mapped = COUNTRY_TO_REGION[countryCode] || 'US';
        setRegionCode(mapped);
        localStorage.setItem('fozzil-region', mapped);
      } catch {
        // Default to US if detection fails
        setRegionCode('US');
        localStorage.setItem('fozzil-region', 'US');
      } finally {
        setIsDetecting(false);
      }
    };

    detectFromIP();
  }, [regionCode]);

  const region = REGIONS[regionCode] || REGIONS.US;

  const formatPrice = (amount) => {
    try {
      return new Intl.NumberFormat(region.locale, {
        style: 'currency',
        currency: region.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${region.symbol}${amount.toLocaleString()}`;
    }
  };

  const changeRegion = (code) => {
    if (REGIONS[code]) {
      setRegionCode(code);
      localStorage.setItem('fozzil-region', code);
    }
  };

  return (
    <RegionContext.Provider value={{
      region,
      regionCode: regionCode || 'US',
      regions: REGIONS,
      formatPrice,
      changeRegion,
      isDetecting,
    }}>
      {children}
    </RegionContext.Provider>
  );
};

export default RegionContext;
